import { Cookie } from 'tough-cookie';
import {
  bearerToken,
  bearerToken2,
  FetchTransformOptions,
  RequestApiResult,
} from './api';
import { TwitterAuth, TwitterAuthOptions, TwitterGuestAuth } from './auth';
import { FlowSubtaskHandler, TwitterUserAuth } from './auth-user';
import type { BrowserProfile } from './castle';
import { getProfile, getUserIdByScreenName, Profile } from './profile';
import {
  fetchSearchProfiles,
  fetchSearchTweets,
  SearchMode,
  searchProfiles,
  searchTweets,
} from './search';
import {
  fetchProfileFollowing,
  fetchProfileFollowers,
  getFollowing,
  getFollowers,
} from './relationships';
import { QueryProfilesResponse, QueryTweetsResponse } from './timeline-v1';
import { getTrends } from './trends';
import {
  Tweet,
  getTweetAnonymous,
  getTweets,
  getLatestTweet,
  getLikedTweets,
  getTweetWhere,
  getTweetsWhere,
  getTweetsByUserId,
  TweetQuery,
  getTweet,
  fetchListTweets,
  getTweetsAndRepliesByUserId,
  getTweetsAndReplies,
  fetchLikedTweets,
} from './tweets';
import fetch from 'cross-fetch';
import debug from 'debug';
import { RateLimitStrategy } from './rate-limit';
import {
  DmConversationTimeline,
  DmInbox,
  DmMessageEntry,
  DmCursorOptions,
  getDmConversation,
  getDmMessages,
  getDmInbox,
  findDmConversationsByUserId,
  DmConversation,
} from './direct-messages';

const log = debug('twitter-scraper:scraper');
const twUrl = 'https://x.com';

export interface ScraperOptions {
  /**
   * An alternative fetch function to use instead of the default fetch function. This may be useful
   * in nonstandard runtime environments, such as edge workers.
   */
  fetch: typeof fetch;

  /**
   * Additional options that control how requests and responses are processed. This can be used to
   * proxy requests through other hosts, for example.
   */
  transform: Partial<FetchTransformOptions>;

  /**
   * A handling strategy for rate limits (HTTP 429).
   */
  rateLimitStrategy: RateLimitStrategy;

  /**
   * Experimental features that may be added, changed, or removed at any time. Use with caution.
   */
  experimental: {
    /**
     * Enables the generation of the `x-client-transaction-id` header on requests. This may resolve some errors.
     */
    xClientTransactionId: boolean;
    /**
     * Enables the generation of the `x-xp-forwarded-for` header on requests. This may resolve some errors.
     */
    xpff: boolean;
    /**
     * Delay in milliseconds between login flow steps, to mimic human-like timing.
     * Without a delay, Twitter may flag rapid-fire requests as bot activity (error 399).
     * Set to 0 to disable. Default is 1-3 seconds (average ~2s) with random jitter.
     */
    flowStepDelay?: number;
    /**
     * Override the browser profile used for Castle.io fingerprint token generation.
     * Unspecified fields are randomized from realistic value pools.
     * Set this if you want a consistent fingerprint or need to match specific hardware.
     */
    browserProfile?: Partial<BrowserProfile>;
  };
}

/**
 * An interface to Twitter's undocumented API.
 * - Reusing Scraper objects is recommended to minimize the time spent authenticating unnecessarily.
 */
export class Scraper {
  private auth!: TwitterAuth;
  private authTrends!: TwitterAuth;
  private token: string;
  private readonly subtaskHandlers: Map<string, FlowSubtaskHandler> = new Map();

  /**
   * Creates a new Scraper object.
   * - Scrapers maintain their own guest tokens for Twitter's internal API.
   * - Reusing Scraper objects is recommended to minimize the time spent authenticating unnecessarily.
   */
  constructor(private readonly options?: Partial<ScraperOptions>) {
    this.token = bearerToken;
    this.useGuestAuth();
  }

  /**
   * Registers a subtask handler for the given subtask ID. This
   * will override any existing handler for the same subtask.
   * @param subtaskId The ID of the subtask to register the handler for.
   * @param subtaskHandler The handler function to register.
   */
  public registerAuthSubtaskHandler(
    subtaskId: string,
    subtaskHandler: FlowSubtaskHandler,
  ): void {
    // Always store so handlers survive auth transitions (guest -> user)
    this.subtaskHandlers.set(subtaskId, subtaskHandler);

    if (this.auth instanceof TwitterUserAuth) {
      this.auth.registerSubtaskHandler(subtaskId, subtaskHandler);
    }

    if (this.authTrends instanceof TwitterUserAuth) {
      this.authTrends.registerSubtaskHandler(subtaskId, subtaskHandler);
    }
  }

  /**
   * Applies all stored subtask handlers to the given auth instance.
   * @internal
   */
  private applySubtaskHandlers(auth: TwitterUserAuth): void {
    for (const [subtaskId, handler] of this.subtaskHandlers) {
      auth.registerSubtaskHandler(subtaskId, handler);
    }
  }

  /**
   * Initializes auth properties using a guest token.
   * Used when creating a new instance of this class, and when logging out.
   * @internal
   */
  private useGuestAuth() {
    this.auth = new TwitterGuestAuth(this.token, this.getAuthOptions());
    this.authTrends = new TwitterGuestAuth(this.token, this.getAuthOptions());
  }

  /**
   * Fetches a Twitter profile.
   * @param username The Twitter username of the profile to fetch, without an `@` at the beginning.
   * @returns The requested {@link Profile}.
   */
  public async getProfile(username: string): Promise<Profile> {
    const res = await getProfile(username, this.auth);
    return this.handleResponse(res);
  }

  /**
   * Fetches the user ID corresponding to the provided screen name.
   * @param screenName The Twitter screen name of the profile to fetch.
   * @returns The ID of the corresponding account.
   */
  public async getUserIdByScreenName(screenName: string): Promise<string> {
    const res = await getUserIdByScreenName(screenName, this.auth);
    return this.handleResponse(res);
  }

  /**
   * Fetches tweets from Twitter.
   * @param query The search query. Any Twitter-compatible query format can be used.
   * @param maxTweets The maximum number of tweets to return.
   * @param includeReplies Whether or not replies should be included in the response.
   * @param searchMode The category filter to apply to the search. Defaults to `Top`.
   * @returns An {@link AsyncGenerator} of tweets matching the provided filters.
   */
  public searchTweets(
    query: string,
    maxTweets: number,
    searchMode: SearchMode = SearchMode.Top,
  ): AsyncGenerator<Tweet, void> {
    return searchTweets(query, maxTweets, searchMode, this.auth);
  }

  /**
   * Fetches profiles from Twitter.
   * @param query The search query. Any Twitter-compatible query format can be used.
   * @param maxProfiles The maximum number of profiles to return.
   * @returns An {@link AsyncGenerator} of tweets matching the provided filter(s).
   */
  public searchProfiles(
    query: string,
    maxProfiles: number,
  ): AsyncGenerator<Profile, void> {
    return searchProfiles(query, maxProfiles, this.auth);
  }

  /**
   * Fetches tweets from Twitter.
   * @param query The search query. Any Twitter-compatible query format can be used.
   * @param maxTweets The maximum number of tweets to return.
   * @param includeReplies Whether or not replies should be included in the response.
   * @param searchMode The category filter to apply to the search. Defaults to `Top`.
   * @param cursor The search cursor, which can be passed into further requests for more results.
   * @returns A page of results, containing a cursor that can be used in further requests.
   */
  public fetchSearchTweets(
    query: string,
    maxTweets: number,
    searchMode: SearchMode,
    cursor?: string,
  ): Promise<QueryTweetsResponse> {
    return fetchSearchTweets(query, maxTweets, searchMode, this.auth, cursor);
  }

  /**
   * Fetches profiles from Twitter.
   * @param query The search query. Any Twitter-compatible query format can be used.
   * @param maxProfiles The maximum number of profiles to return.
   * @param cursor The search cursor, which can be passed into further requests for more results.
   * @returns A page of results, containing a cursor that can be used in further requests.
   */
  public fetchSearchProfiles(
    query: string,
    maxProfiles: number,
    cursor?: string,
  ): Promise<QueryProfilesResponse> {
    return fetchSearchProfiles(query, maxProfiles, this.auth, cursor);
  }

  /**
   * Fetches list tweets from Twitter.
   * @param listId The list id
   * @param maxTweets The maximum number of tweets to return.
   * @param cursor The search cursor, which can be passed into further requests for more results.
   * @returns A page of results, containing a cursor that can be used in further requests.
   */
  public fetchListTweets(
    listId: string,
    maxTweets: number,
    cursor?: string,
  ): Promise<QueryTweetsResponse> {
    return fetchListTweets(listId, maxTweets, cursor, this.auth);
  }

  /**
   * Fetch the tweets a user has liked
   * @param userId The user whose liked tweets should be returned
   * @param maxTweets The maximum number of tweets to return.
   * @param cursor The search cursor, which can be passed into further requests for more results.
   * @returns A page of results, containing a cursor that can be used in further requests.
   */
  public fetchLikedTweets(
    userId: string,
    maxTweets: number,
    cursor?: string,
  ): Promise<QueryTweetsResponse> {
    return fetchLikedTweets(userId, maxTweets, cursor, this.auth);
  }

  /**
   * Fetch the profiles a user is following
   * @param userId The user whose following should be returned
   * @param maxProfiles The maximum number of profiles to return.
   * @returns An {@link AsyncGenerator} of following profiles for the provided user.
   */
  public getFollowing(
    userId: string,
    maxProfiles: number,
  ): AsyncGenerator<Profile, void> {
    return getFollowing(userId, maxProfiles, this.auth);
  }

  /**
   * Fetch the profiles that follow a user
   * @param userId The user whose followers should be returned
   * @param maxProfiles The maximum number of profiles to return.
   * @returns An {@link AsyncGenerator} of profiles following the provided user.
   */
  public getFollowers(
    userId: string,
    maxProfiles: number,
  ): AsyncGenerator<Profile, void> {
    return getFollowers(userId, maxProfiles, this.auth);
  }

  /**
   * Fetches following profiles from Twitter.
   * @param userId The user whose following should be returned
   * @param maxProfiles The maximum number of profiles to return.
   * @param cursor The search cursor, which can be passed into further requests for more results.
   * @returns A page of results, containing a cursor that can be used in further requests.
   */
  public fetchProfileFollowing(
    userId: string,
    maxProfiles: number,
    cursor?: string,
  ): Promise<QueryProfilesResponse> {
    return fetchProfileFollowing(userId, maxProfiles, this.auth, cursor);
  }

  /**
   * Fetches profile followers from Twitter.
   * @param userId The user whose following should be returned
   * @param maxProfiles The maximum number of profiles to return.
   * @param cursor The search cursor, which can be passed into further requests for more results.
   * @returns A page of results, containing a cursor that can be used in further requests.
   */
  public fetchProfileFollowers(
    userId: string,
    maxProfiles: number,
    cursor?: string,
  ): Promise<QueryProfilesResponse> {
    return fetchProfileFollowers(userId, maxProfiles, this.auth, cursor);
  }

  /**
   * Fetches the current trends from Twitter.
   * @returns The current list of trends.
   */
  public getTrends(): Promise<string[]> {
    return getTrends(this.authTrends);
  }

  /**
   * Fetches tweets from a Twitter user.
   * @param user The user whose tweets should be returned.
   * @param maxTweets The maximum number of tweets to return. Defaults to `200`.
   * @returns An {@link AsyncGenerator} of tweets from the provided user.
   */
  public getTweets(user: string, maxTweets = 200): AsyncGenerator<Tweet> {
    return getTweets(user, maxTweets, this.auth);
  }

  /**
   * Fetches liked tweets from a Twitter user. Requires authentication.
   * @param user The user whose likes should be returned.
   * @param maxTweets The maximum number of tweets to return. Defaults to `200`.
   * @returns An {@link AsyncGenerator} of liked tweets from the provided user.
   */
  public getLikedTweets(user: string, maxTweets = 200): AsyncGenerator<Tweet> {
    return getLikedTweets(user, maxTweets, this.auth);
  }

  /**
   * Fetches tweets from a Twitter user using their ID.
   * @param userId The user whose tweets should be returned.
   * @param maxTweets The maximum number of tweets to return. Defaults to `200`.
   * @returns An {@link AsyncGenerator} of tweets from the provided user.
   */
  public getTweetsByUserId(
    userId: string,
    maxTweets = 200,
  ): AsyncGenerator<Tweet, void> {
    return getTweetsByUserId(userId, maxTweets, this.auth);
  }

  /**
   * Fetches tweets and replies from a Twitter user.
   * @param user The user whose tweets should be returned.
   * @param maxTweets The maximum number of tweets to return. Defaults to `200`.
   * @returns An {@link AsyncGenerator} of tweets from the provided user.
   */
  public getTweetsAndReplies(
    user: string,
    maxTweets = 200,
  ): AsyncGenerator<Tweet> {
    return getTweetsAndReplies(user, maxTweets, this.auth);
  }

  /**
   * Fetches tweets and replies from a Twitter user using their ID.
   * @param userId The user whose tweets should be returned.
   * @param maxTweets The maximum number of tweets to return. Defaults to `200`.
   * @returns An {@link AsyncGenerator} of tweets from the provided user.
   */
  public getTweetsAndRepliesByUserId(
    userId: string,
    maxTweets = 200,
  ): AsyncGenerator<Tweet, void> {
    return getTweetsAndRepliesByUserId(userId, maxTweets, this.auth);
  }

  /**
   * Fetches the first tweet matching the given query.
   *
   * Example:
   * ```js
   * const timeline = scraper.getTweets('user', 200);
   * const retweet = await scraper.getTweetWhere(timeline, { isRetweet: true });
   * ```
   * @param tweets The {@link AsyncIterable} of tweets to search through.
   * @param query A query to test **all** tweets against. This may be either an
   * object of key/value pairs or a predicate. If this query is an object, all
   * key/value pairs must match a {@link Tweet} for it to be returned. If this query
   * is a predicate, it must resolve to `true` for a {@link Tweet} to be returned.
   * - All keys are optional.
   * - If specified, the key must be implemented by that of {@link Tweet}.
   */
  public getTweetWhere(
    tweets: AsyncIterable<Tweet>,
    query: TweetQuery,
  ): Promise<Tweet | null> {
    return getTweetWhere(tweets, query);
  }

  /**
   * Fetches all tweets matching the given query.
   *
   * Example:
   * ```js
   * const timeline = scraper.getTweets('user', 200);
   * const retweets = await scraper.getTweetsWhere(timeline, { isRetweet: true });
   * ```
   * @param tweets The {@link AsyncIterable} of tweets to search through.
   * @param query A query to test **all** tweets against. This may be either an
   * object of key/value pairs or a predicate. If this query is an object, all
   * key/value pairs must match a {@link Tweet} for it to be returned. If this query
   * is a predicate, it must resolve to `true` for a {@link Tweet} to be returned.
   * - All keys are optional.
   * - If specified, the key must be implemented by that of {@link Tweet}.
   */
  public getTweetsWhere(
    tweets: AsyncIterable<Tweet>,
    query: TweetQuery,
  ): Promise<Tweet[]> {
    return getTweetsWhere(tweets, query);
  }

  /**
   * Fetches the most recent tweet from a Twitter user.
   * @param user The user whose latest tweet should be returned.
   * @param includeRetweets Whether or not to include retweets. Defaults to `false`.
   * @returns The {@link Tweet} object or `null`/`undefined` if it couldn't be fetched.
   */
  public getLatestTweet(
    user: string,
    includeRetweets = false,
    max = 200,
  ): Promise<Tweet | null | void> {
    return getLatestTweet(user, includeRetweets, max, this.auth);
  }

  /**
   * Fetches a single tweet.
   * @param id The ID of the tweet to fetch.
   * @returns The {@link Tweet} object, or `null` if it couldn't be fetched.
   */
  public getTweet(id: string): Promise<Tweet | null> {
    if (this.auth instanceof TwitterUserAuth) {
      return getTweet(id, this.auth);
    } else {
      return getTweetAnonymous(id, this.auth);
    }
  }

  /**
   * Retrieves the direct message inbox for the authenticated user.
   *
   * @return A promise that resolves to an object representing the direct message inbox.
   */
  public getDmInbox(): Promise<DmInbox> {
    return getDmInbox(this.auth);
  }

  /**
   * Retrieves the direct message conversation for the specified conversation ID.
   *
   * @param conversationId - The unique identifier of the DM conversation to retrieve.
   * @param cursor - Use `maxId` to get messages before a message ID (older messages), or `minId` to get messages after a message ID (newer messages).
   * @return A promise that resolves to the timeline of the DM conversation.
   */
  public getDmConversation(
    conversationId: string,
    cursor?: DmCursorOptions,
  ): Promise<DmConversationTimeline> {
    return getDmConversation(conversationId, cursor, this.auth);
  }

  /**
   * Retrieves direct messages from a specific conversation.
   *
   * @param conversationId - The unique identifier of the conversation to fetch messages from.
   * @param [maxMessages=20] - The maximum number of messages to retrieve per request.
   * @param cursor - Use `maxId` to get messages before a message ID (older messages), or `minId` to get messages after a message ID (newer messages).
   * @returns An {@link AsyncGenerator} of messages from the provided conversation.
   */
  public getDmMessages(
    conversationId: string,
    maxMessages = 20,
    cursor?: DmCursorOptions,
  ): AsyncGenerator<DmMessageEntry, void> {
    return getDmMessages(conversationId, maxMessages, cursor, this.auth);
  }

  /**
   * Retrieves a list of direct message conversations for a specific user based on their user ID.
   *
   * @param inbox - The DM inbox containing all available conversations.
   * @param userId - The unique identifier of the user whose DM conversations are to be retrieved.
   * @return An array of DM conversations associated with the specified user ID.
   */
  public findDmConversationsByUserId(
    inbox: DmInbox,
    userId: string,
  ): DmConversation[] {
    return findDmConversationsByUserId(inbox, userId);
  }

  /**
   * Returns if the scraper has a guest token. The token may not be valid.
   * @returns `true` if the scraper has a guest token; otherwise `false`.
   */
  public hasGuestToken(): boolean {
    return this.auth.hasToken() || this.authTrends.hasToken();
  }

  /**
   * Returns if the scraper is logged in as a real user.
   * @returns `true` if the scraper is logged in with a real user account; otherwise `false`.
   */
  public async isLoggedIn(): Promise<boolean> {
    return (
      (await this.auth.isLoggedIn()) && (await this.authTrends.isLoggedIn())
    );
  }

  /**
   * Login to Twitter as a real Twitter account. This enables running
   * searches.
   * @param username The username of the Twitter account to login with.
   * @param password The password of the Twitter account to login with.
   * @param email The email to log in with, if you have email confirmation enabled.
   * @param twoFactorSecret The secret to generate two factor authentication tokens with, if you have two factor authentication enabled.
   */
  public async login(
    username: string,
    password: string,
    email?: string,
    twoFactorSecret?: string,
  ): Promise<void> {
    // Swap in a real authorizer for all requests
    // Use bearerToken2 for the login flow - this is the same token the Twitter frontend uses.
    // Guest activation and the onboarding/task.json endpoint both require this token.
    const userAuth = new TwitterUserAuth(bearerToken2, this.getAuthOptions());
    this.applySubtaskHandlers(userAuth);
    await userAuth.login(username, password, email, twoFactorSecret);
    this.auth = userAuth;
    this.authTrends = userAuth;
  }

  /**
   * Log out of Twitter.
   */
  public async logout(): Promise<void> {
    await this.auth.logout();
    await this.authTrends.logout();

    // Swap in guest authorizers for all requests
    this.useGuestAuth();
  }

  /**
   * Retrieves all cookies for the current session.
   * @returns All cookies for the current session.
   */
  public async getCookies(): Promise<Cookie[]> {
    return await this.authTrends
      .cookieJar()
      .getCookies(
        typeof document !== 'undefined' ? document.location.toString() : twUrl,
      );
  }

  /**
   * Set cookies for the current session.
   * @param cookies The cookies to set for the current session.
   */
  public async setCookies(cookies: (string | Cookie)[]): Promise<void> {
    // Use bearerToken2 for authenticated user requests
    const userAuth = new TwitterUserAuth(bearerToken2, this.getAuthOptions());
    this.applySubtaskHandlers(userAuth);
    for (const cookie of cookies) {
      if (cookie == null) continue;

      if (typeof cookie === 'string') {
        // String cookies are parsed by tough-cookie, which normalizes domains correctly
        try {
          await userAuth.cookieJar().setCookie(cookie, 'https://x.com');
        } catch (err) {
          log(`Failed to parse cookie string: ${(err as Error).message}`);
        }
      } else {
        // Cookie objects from Cookie.fromJSON() preserve the domain literally.
        // tough-cookie's getCookies() won't match ".x.com" against "https://x.com",
        // so we must strip the leading dot and set hostOnly=false to allow
        // subdomain matching (matching how tough-cookie normalizes string cookies).
        if (cookie.domain && cookie.domain.startsWith('.')) {
          cookie.domain = cookie.domain.substring(1);
          cookie.hostOnly = false;
        }

        const cookieDomain = cookie.domain || 'x.com';
        const cookieUrl = `https://${cookieDomain}`;
        await userAuth.cookieJar().setCookie(cookie, cookieUrl);
      }
    }

    this.auth = userAuth;
    this.authTrends = userAuth;

    // Warn if auth_token is missing - this is the most common cause of 401 errors.
    // auth_token is an HttpOnly cookie that cannot be accessed via document.cookie;
    // it must be exported using a browser extension or DevTools.
    const isLoggedIn = await userAuth.isLoggedIn();
    if (!isLoggedIn) {
      const cookieString = await userAuth
        .cookieJar()
        .getCookies(twUrl)
        .then((c) => c.map((cookie) => cookie.key));
      if (
        cookieString.includes('ct0') &&
        !cookieString.includes('auth_token')
      ) {
        log(
          'auth_token cookie is missing. This is required for authenticated API access. ' +
            'The auth_token is an HttpOnly cookie that cannot be accessed via document.cookie. ' +
            'Export it using a browser extension (e.g., EditThisCookie) or DevTools Application tab.',
        );
      }
    }
  }

  /**
   * Clear all cookies for the current session.
   */
  public async clearCookies(): Promise<void> {
    await this.auth.cookieJar().removeAllCookies();
    await this.authTrends.cookieJar().removeAllCookies();
  }

  /**
   * Sets the optional cookie to be used in requests.
   * @param _cookie The cookie to be used in requests.
   * @deprecated This function no longer represents any part of Twitter's auth flow.
   * @returns This scraper instance.
   */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  public withCookie(_cookie: string): Scraper {
    console.warn(
      'Warning: Scraper#withCookie is deprecated and will be removed in a later version. Use Scraper#login or Scraper#setCookies instead.',
    );
    return this;
  }

  /**
   * Sets the optional CSRF token to be used in requests.
   * @param _token The CSRF token to be used in requests.
   * @deprecated This function no longer represents any part of Twitter's auth flow.
   * @returns This scraper instance.
   */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  public withXCsrfToken(_token: string): Scraper {
    console.warn(
      'Warning: Scraper#withXCsrfToken is deprecated and will be removed in a later version.',
    );
    return this;
  }

  private getAuthOptions(): Partial<TwitterAuthOptions> {
    return {
      fetch: this.options?.fetch,
      transform: this.options?.transform,
      rateLimitStrategy: this.options?.rateLimitStrategy,
      experimental: {
        xClientTransactionId: this.options?.experimental?.xClientTransactionId,
        xpff: this.options?.experimental?.xpff,
        flowStepDelay: this.options?.experimental?.flowStepDelay,
        browserProfile: this.options?.experimental?.browserProfile,
      },
    };
  }

  private handleResponse<T>(res: RequestApiResult<T>): T {
    if (!res.success) {
      throw res.err;
    }

    return res.value;
  }
}
